import { useState, useEffect } from 'react';
import Select, { components } from 'react-select';
import { useTranslation } from "react-i18next";

const Option = (props) => {
    const { data, selectProps } = props;
    const { isMultiple, tempValue, setTempValue } = selectProps;

    if (!isMultiple) {
        return <components.Option {...props}>{data.label}</components.Option>;
    }

    const isSelected = tempValue.some((v) => v.value === data.value);
    return (
        <div
            style={{ backgroundColor: isSelected ? "#d6e8ff" : "#fff" }}
            onClick={(e) => {
                e.stopPropagation();
                let newValue;
                if (isSelected) {
                    newValue = tempValue.filter((v) => v.value !== data.value);
                } else {
                    newValue = [...tempValue, data];
                }
                setTempValue(newValue);
            }}
            className="d-flex align-items-center p-2 al-custom-select-option"
        >
            <input type="checkbox" className="form-check-input" checked={isSelected} readOnly />
            <label style={{ marginLeft: 6 }}>{data.label}</label>
        </div>
    );
};

const MenuList = (props) => {
    const { t } = useTranslation();
    const { children, selectProps } = props;
    const { isMultiple, setIsMultiple, mode } = selectProps;

    return (
        <components.MenuList {...props}>
            {children}
            <div className="d-flex align-items-center justify-content-between position-sticky bg-white bottom-0 p-2" style={{ borderTop: mode !== "single" ? "1px solid #ddd" : "0px solid #ddd" }}>
                <div className="d-flex gap-2">
                    {!mode && (
                        <button
                            type="button"
                            className="btn btn-info btn-sm me-1"
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsMultiple(!isMultiple);

                                selectProps.setTempValue([]);
                                selectProps.setFinalValue([]);
                                selectProps.onChange?.([]);
                            }}
                        >
                            <i className="ki-duotone ki-arrow-right-left">
                                <span className="path1"></span>
                                <span className="path2"></span>
                            </i> {isMultiple ? t("Single") : t("Multiple")}
                        </button>
                    )}
                </div>

                {isMultiple && (
                    <div className="d-flex gap-2">
                        <button
                            type="button"
                            className="btn btn-light btn-sm me-1"
                            onClick={(e) => {
                                e.stopPropagation();
                                selectProps.onCancel();
                                selectProps.setMenuIsOpen(false);
                            }}
                        >
                            {t("Cancel")}
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary btn-sm me-1"
                            onClick={(e) => {
                                e.stopPropagation();
                                selectProps.onConfirm();
                                selectProps.setMenuIsOpen(false);
                            }}
                        >
                            {t("Confirm")}
                        </button>
                    </div>
                )}
            </div>
        </components.MenuList>
    );
};

const ValueContainer = ({ children, getValue, ...props }) => {
    const values = getValue();

    if (!props.selectProps.isMulti || !props?.selectProps?.shortValue) {
        return (
            <components.ValueContainer {...props}>
                {children}
            </components.ValueContainer>
        );
    }

    let displayLabel = "";
    if (values.length === 1) {
        displayLabel = values[0].label;
    } else if (values.length > 1) {
        displayLabel = `${values.length} selected`;
    }

    return (
        <components.ValueContainer {...props}>
            <div className="custom-value">{displayLabel}</div>
            {children[1]}
        </components.ValueContainer>
    );
};

const SelectCustom = ({ mode = "", options, value, onChange, placeholder = "", className = "", shortValue = true, isDisabled = false }) => {
    const { t } = useTranslation();
    const [isMultiple, setIsMultiple] = useState(mode === "multiple");
    const [tempValue, setTempValue] = useState(mode === "multiple" ? [] : null);
    const [finalValue, setFinalValue] = useState(value || (mode === "multiple" ? [] : null));
    const [menuIsOpen, setMenuIsOpen] = useState(false);

    useEffect(() => {
        if (mode === "multiple") {
            setIsMultiple(true);
            setTempValue([]);
            setFinalValue([]);
        }
        if (mode === "single") {
            setIsMultiple(false);
            setTempValue(null);
            setFinalValue(null);
        }
    }, [mode]);

    useEffect(() => {
        if (value) {
            setFinalValue(value);
            setTempValue(value);
        }
    }, [value]);

    const handleConfirm = () => {
        setFinalValue(tempValue);
        onChange?.(tempValue);
    };

    const handleCancel = () => {
        setTempValue([]);
        setFinalValue([]);
        onChange?.([]);
    };

    return (
        <Select
            isMulti={isMultiple}
            isClearable
            options={options}
            closeMenuOnSelect={!isMultiple}
            hideSelectedOptions={false}
            components={{ Option, MenuList, ValueContainer }}
            value={finalValue}
            menuIsOpen={menuIsOpen}
            onMenuOpen={() => setMenuIsOpen(true)}
            onMenuClose={() => setMenuIsOpen(false)}
            className={"basic-multi-select al-custom-select-search-variants mr-1 mb-0 al-filter-table me-2 " + className}
            classNamePrefix="select"
            placeholder={placeholder || t("Select ...")}

            isDisabled={isDisabled}
            shortValue={shortValue}
            mode={mode}
            isMultiple={isMultiple}
            setIsMultiple={setIsMultiple}
            tempValue={tempValue}
            setTempValue={setTempValue}
            finalValue={finalValue}
            setFinalValue={setFinalValue}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
            setMenuIsOpen={setMenuIsOpen}
            onChange={(value, action) => {
                if (action?.action === "clear") {
                    setFinalValue([]);
                    setTempValue([]);
                    onChange?.([]);
                }
                if (action?.action === "remove-value" || action?.action === "pop-value") {
                    setFinalValue(value || []);
                    setTempValue(value || []);
                    onChange?.(value || []);
                }
                if (!isMultiple) {
                    if (Array.isArray(value)) {
                        setTempValue(value);
                        setFinalValue(value);
                        // onChange?.(value);
                    } else if (value) {
                        setTempValue([value]);
                        setFinalValue([value]);
                        onChange?.([value]);
                    } else {
                        setTempValue([]);
                        setFinalValue([]);
                        onChange?.([]);
                    }
                }
            }}

            styles={{
                menu: (provided) => ({
                    ...provided,
                    minWidth: "450px",
                    zIndex: "9999"
                }),
                menuList: (provided) => ({
                    ...provided,
                    paddingBottom: "0px"
                }),
            }}
        />
    );
};

export default SelectCustom;